import React from 'react';
import { Calendar, MoreVertical, CheckCircle, XCircle } from 'lucide-react';
import CustomSelect from '../../ui/CustomSelect';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../../ui/dropdown-menu';

const statusColors = {
  pending: 'bg-amber-100 text-amber-700',
  approved: 'bg-green-100 text-green-700',
  rejected: 'bg-red-100 text-red-700',
  cancelled: 'bg-gray-100 text-gray-700',
};

const statusLabels = {
  pending: 'Menunggu',
  approved: 'Disetujui',
  rejected: 'Ditolak',
  cancelled: 'Dibatalkan',
};

const leaveTypeLabels = {
  annual: 'Cuti Tahunan',
  sick: 'Sakit',
  maternity: 'Cuti Melahirkan',
  paternity: 'Cuti Ayah',
  unpaid: 'Tanpa Gaji',
  other: 'Lainnya',
};

const filterOptions = [
  { value: 'all', label: 'Semua Status' },
  { value: 'pending', label: 'Menunggu' },
  { value: 'approved', label: 'Disetujui' },
  { value: 'rejected', label: 'Ditolak' },
];

function LoadingSkeleton() {
  return Array(5)
    .fill(0)
    .map((_, i) => (
      <tr key={i} className="border-b">
        <td className="px-4 py-3">
          <div className="h-10 w-40 bg-gray-200 rounded animate-pulse"></div>
        </td>
        <td className="px-4 py-3 hidden md:table-cell">
          <div className="h-6 w-28 bg-gray-200 rounded animate-pulse"></div>
        </td>
        <td className="px-4 py-3 hidden lg:table-cell">
          <div className="h-4 w-36 bg-gray-200 rounded animate-pulse"></div>
        </td>
        <td className="px-4 py-3 hidden md:table-cell">
          <div className="h-4 w-16 bg-gray-200 rounded animate-pulse"></div>
        </td>
        <td className="px-4 py-3">
          <div className="h-6 w-24 bg-gray-200 rounded animate-pulse"></div>
        </td>
        <td className="px-4 py-3">
          <div className="h-8 w-8 bg-gray-200 rounded animate-pulse"></div>
        </td>
      </tr>
    ));
}

function EmptyState() {
  return (
    <tr>
      <td colSpan={6} className="text-center py-12">
        <Calendar className="w-12 h-12 mx-auto mb-3 text-gray-300" />
        <p className="text-gray-500">Tidak ada pengajuan cuti</p>
      </td>
    </tr>
  );
}

function LeaveRow({ leave, getEmployeeName, onApprove, onReject, formatDate }) {
  return (
    <tr className="border-b hover:bg-gray-50">
      <td className="px-4 py-3">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center flex-shrink-0">
            <span className="font-semibold text-purple-700">
              {getEmployeeName(leave.employee_id)?.charAt(0)}
            </span>
          </div>
          <span className="font-medium truncate">{getEmployeeName(leave.employee_id)}</span>
        </div>
      </td>
      <td className="px-4 py-3 hidden md:table-cell">
        <span className="px-2 py-1 text-xs font-medium rounded-full border">
          {leaveTypeLabels[leave.leave_type] || leave.leave_type}
        </span>
      </td>
      <td className="px-4 py-3 hidden lg:table-cell text-sm">
        {formatDate(leave.start_date)} - {formatDate(leave.end_date)}
      </td>
      <td className="px-4 py-3 hidden md:table-cell">{leave.total_days} hari</td>
      <td className="px-4 py-3">
        <span
          className={`px-2 py-1 text-xs font-medium rounded-full ${statusColors[leave.status]}`}
        >
          {statusLabels[leave.status]}
        </span>
      </td>
      <td className="px-4 py-3">
        {leave.status === 'pending' && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="p-2 hover:bg-gray-100 rounded-lg">
                <MoreVertical className="w-4 h-4" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => onApprove(leave)}>
                <CheckCircle className="w-4 h-4 mr-2 text-green-600" /> Setujui
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => onReject(leave)}>
                <XCircle className="w-4 h-4 mr-2 text-red-600" /> Tolak
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </td>
    </tr>
  );
}

export default function LeaveTable({
  leaves,
  loading,
  filterStatus,
  onFilterChange,
  getEmployeeName,
  onApprove,
  onReject,
}) {
  const formatDate = dateStr => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('id-ID', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  };

  return (
    <div className="bg-white rounded-lg border shadow-sm">
      <div className="p-4 border-b flex flex-col md:flex-row md:items-center justify-between gap-4">
        <h3 className="font-semibold">Daftar Pengajuan Cuti</h3>
        <CustomSelect
          value={filterStatus}
          onChange={onFilterChange}
          options={filterOptions}
          placeholder="Semua Status"
          width="w-full md:w-44"
        />
      </div>
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="bg-gray-50 border-b">
              <th className="text-left px-4 py-3 text-sm font-medium text-gray-700">Pegawai</th>
              <th className="text-left px-4 py-3 text-sm font-medium text-gray-700 hidden md:table-cell">
                Jenis Cuti
              </th>
              <th className="text-left px-4 py-3 text-sm font-medium text-gray-700 hidden lg:table-cell">
                Tanggal
              </th>
              <th className="text-left px-4 py-3 text-sm font-medium text-gray-700 hidden md:table-cell">
                Durasi
              </th>
              <th className="text-left px-4 py-3 text-sm font-medium text-gray-700">Status</th>
              <th className="w-12 px-4 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <LoadingSkeleton />
            ) : leaves.length === 0 ? (
              <EmptyState />
            ) : (
              leaves.map(leave => (
                <LeaveRow
                  key={leave.id}
                  leave={leave}
                  getEmployeeName={getEmployeeName}
                  onApprove={onApprove}
                  onReject={onReject}
                  formatDate={formatDate}
                />
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
